import { SERVICE_TYPE_LABELS, type ServiceType } from "@garage/shared";
import type { ServiceListResponse } from "../api/services";
import { Button, inputClass, Label } from "./ui";

export interface Filters {
  year?: string;
  types?: string;
  minCost?: string;
  maxCost?: string;
  q?: string;
  sort?: string;
  page?: string;
  // Filters are read straight out of the query string, so the shape has to
  // tolerate keys this UI does not know about.
  [key: string]: string | undefined;
}

const SORTS = [
  ["date:desc", "Newest first"],
  ["date:asc", "Oldest first"],
  ["cost:desc", "Most expensive"],
  ["cost:asc", "Cheapest"],
  ["odometer:desc", "Highest odometer"],
] as const;

export function ServiceFilters({
  filters,
  facets,
  onChange,
}: {
  filters: Filters;
  facets: ServiceListResponse["facets"] | undefined;
  onChange: (next: Filters) => void;
}) {
  // Any filter change resets to page 1: staying on page 3 of a result set
  // that now has one page shows an empty list and looks broken.
  const patch = (next: Partial<Filters>) =>
    onChange({ ...filters, ...next, page: undefined });

  const selectedTypes = filters.types ? filters.types.split(",") : [];

  const toggleType = (type: ServiceType) =>
    patch({
      types:
        (selectedTypes.includes(type)
          ? selectedTypes.filter((t) => t !== type)
          : [...selectedTypes, type]
        ).join(",") || undefined,
    });

  const active =
    Boolean(filters.year || filters.types || filters.minCost || filters.maxCost || filters.q);

  return (
    <div className="flex flex-col gap-3 rounded border border-line bg-surface p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-40 flex-1">
          <Label>Search</Label>
          <input
            className={`${inputClass} mt-1 !py-1.5 text-sm`}
            value={filters.q ?? ""}
            onChange={(e) => patch({ q: e.target.value || undefined })}
            placeholder="Title, notes, part name…"
          />
        </div>

        <div>
          <Label>Year</Label>
          <select
            className={`${inputClass} mt-1 !py-1.5 text-sm`}
            value={filters.year ?? ""}
            onChange={(e) => patch({ year: e.target.value || undefined })}
          >
            <option value="">Any year</option>
            {/* Years come from the data, not a hardcoded range. */}
            {facets?.years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div>
          <Label>Cost from</Label>
          <input
            className={`${inputClass} mt-1 w-24 !py-1.5 text-sm`}
            value={filters.minCost ?? ""}
            onChange={(e) => patch({ minCost: e.target.value || undefined })}
            inputMode="decimal"
            placeholder="₹0"
          />
        </div>
        <div>
          <Label>to</Label>
          <input
            className={`${inputClass} mt-1 w-24 !py-1.5 text-sm`}
            value={filters.maxCost ?? ""}
            onChange={(e) => patch({ maxCost: e.target.value || undefined })}
            inputMode="decimal"
            placeholder="Any"
          />
        </div>

        <div>
          <Label>Sort</Label>
          <select
            className={`${inputClass} mt-1 !py-1.5 text-sm`}
            value={filters.sort ?? "date:desc"}
            onChange={(e) => patch({ sort: e.target.value })}
          >
            {SORTS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {facets?.types.map(({ type, count }) => {
          const on = selectedTypes.includes(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => toggleType(type)}
              aria-pressed={on}
              className={`rounded border px-2.5 py-1 font-mono text-[11px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-signal ${
                on
                  ? "border-signal bg-signal/15 text-signal"
                  : "border-line text-ink-3 hover:text-ink"
              }`}
            >
              {SERVICE_TYPE_LABELS[type]}
              <span className="ml-1.5 opacity-60">{count}</span>
            </button>
          );
        })}

        {active && (
          <Button
            type="button"
            variant="ghost"
            className="ml-auto !px-2.5 !py-1 text-xs"
            onClick={() => onChange({ sort: filters.sort })}
          >
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}
